'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Backdrop, Button, CircularProgress, Dialog, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Select, Switch } from '@mui/material'
import moment from 'moment-timezone'
import { AnimatePresence, motion } from 'motion/react'
import { getTimezoneLabel, resolveToDropdownTimezone } from '@/constants/timezones.constant'
import { DateTimeObj, InterviewPanelistType } from '@/app/types/types'
import { TimezoneSelectMenuItems } from '@/app/components/TimezoneSelectMenuItems'
import { useToast } from '@/app/context/toast-provider'
import { getAvailableSlotsRequest } from '@/app/utils/handlers/client/calendar'
import { requestHandler } from '@/app/utils/requestHandler'
import BookWeeklyCalendar, { AvailabilitySlot, BusyCalendarEventInput, SelectedRange, SelectRangeMeta } from './BookWeeklyCalendar'
import { LuCalendar, LuCircleCheck, LuX } from 'react-icons/lu'
import { LocationPicker } from '@/components/ui'
import { TIME_FORMAT } from '@/constants/time-format.constant'

const generateTimeOptions = () => {
    const options: { value: string; label: string }[] = []
    for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 5) {
            const time = moment().hour(hour).minute(minute).second(0)
            options.push({
                value: time.format('HH:mm'),
                label: time.format(TIME_FORMAT.TIME),
            })
        }
    }
    return options
}

const TIME_OPTIONS = generateTimeOptions()
const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120] as const
const DEFAULT_DURATION = 30

const snapToDurationOption = (minutes: number) =>
    DURATION_OPTIONS.reduce((best, option) => (Math.abs(option - minutes) < Math.abs(best - minutes) ? option : best))

/** Prefer common abbreviations (IST, GMT, EST); fall back to offset like +05:30. */
const formatTimezoneShort = (tzid: string, at?: Date) => {
    const m = at ? moment.tz(at, tzid) : moment.tz(tzid)
    const abbr = m.format('z')
    if (abbr && /^[A-Za-z]{2,5}$/.test(abbr)) return abbr
    return m.format('Z')
}

export type BookInterviewModalProps = {
    open: boolean
    onClose: () => void
    interviewRound: InterviewPanelistType
    companySlug: string
    candidateEmail?: string
    jobPostingId: number
    /** Job posting country — pins that country's timezone in the dropdown when mappable. */
    jobPostingCountry?: string | null
    setConfirmationModalOpen: (open: boolean) => void
    setDateTimeSelected: (dateTimeSelected: any) => void
    isFaceToFaceInterview: boolean
    setIsFaceToFaceInterview: (value: boolean) => void
    interviewLocation: string
    setInterviewLocation: (value: string) => void
    /** When false, F2F location input is hidden. Location is optional when shown. */
    showInterviewLocation?: boolean
    hideFaceToFace?: boolean
}

const BookInterviewModal: React.FC<BookInterviewModalProps> = ({
    open,
    onClose,
    interviewRound,
    jobPostingId,
    jobPostingCountry,
    setConfirmationModalOpen,
    setDateTimeSelected,
    isFaceToFaceInterview,
    setIsFaceToFaceInterview,
    interviewLocation,
    setInterviewLocation,
    showInterviewLocation = false,
    hideFaceToFace = false,
}) => {
    const { showErrorToast } = useToast()
    const [availableSlots, setAvailableSlots] = useState<AvailabilitySlot[]>([])
    const [busyEvents, setBusyEvents] = useState<BusyCalendarEventInput[]>([])
    const [loading, setLoading] = useState(false)
    const [timezone, setTimezone] = useState(() => resolveToDropdownTimezone(interviewRound?.timezone))
    const [currentDate, setCurrentDate] = useState<Date>(() => new Date())
    const [selectedRange, setSelectedRange] = useState<SelectedRange>(null)
    const [startDate, setStartDate] = useState('')
    const [startTime, setStartTime] = useState('')
    const [endTime, setEndTime] = useState('')
    const [duration, setDuration] = useState(DEFAULT_DURATION)
    const [conflictInfo, setConflictInfo] = useState<{ hasConflicts: boolean; message: string }>({
        hasConflicts: false,
        message: '',
    })

    useEffect(() => {
        if (!open) return
        setTimezone(resolveToDropdownTimezone(interviewRound?.timezone))
        setSelectedRange(null)
        setStartDate('')
        setStartTime('')
        setEndTime('')
        setDuration(DEFAULT_DURATION)
        setConflictInfo({ hasConflicts: false, message: '' })
        setBusyEvents([])
        setAvailableSlots([])
        // Always open on today's week
        setCurrentDate(new Date())
        // Seed the lifted face-to-face fields from the round so cancelled sessions don't leak stale values
        setIsFaceToFaceInterview(Boolean(interviewRound?.isFaceToFaceInterview))
        setInterviewLocation(interviewRound?.interviewLocation ?? '')
    }, [open, interviewRound, setIsFaceToFaceInterview, setInterviewLocation])

    const panelistEmailsKey = useMemo(
        () =>
            (interviewRound?.PanellistFeedback || [])
                .map((p: any) => p?.email || '')
                .filter((e: string) => e && e.trim().length > 0)
                .join(','),
        [interviewRound?.PanellistFeedback]
    )

    const fetchAvailableSlots = useCallback(
        (date: Date, tz: string, emails: string[]) => {
            const data = {
                emails,
                start: moment(date).startOf('week').toISOString(),
                end: moment(date).endOf('week').add(1, 'week').toISOString(),
                duration,
                tzid: tz,
                jobPostingId,
            }

            requestHandler(
                async () => getAvailableSlotsRequest(data),
                (res) => {
                    setAvailableSlots(res.data?.availableSlots || [])
                    setBusyEvents(res.data?.busyEvents || [])
                },
                (error) => {
                    showErrorToast(error)
                },
                setLoading
            )
        },
        [duration, jobPostingId, showErrorToast]
    )

    useEffect(() => {
        if (!open) return
        const emails = panelistEmailsKey ? panelistEmailsKey.split(',') : []
        fetchAvailableSlots(currentDate, timezone, emails)
    }, [open, currentDate, timezone, panelistEmailsKey, fetchAvailableSlots])

    const applyRangeToForm = (range: { start: Date; end: Date }) => {
        const startM = moment(range.start)
        const endM = moment(range.end)
        setStartDate(startM.format('YYYY-MM-DD'))
        setStartTime(startM.format('HH:mm'))
        setEndTime(endM.format('HH:mm'))
        setSelectedRange(range)
    }

    const handleSelectRange = (range: { start: Date; end: Date }, conflict?: { hasConflicts: boolean; message: string }, meta?: SelectRangeMeta) => {
        if (meta?.isDrag) {
            const snappedDuration = snapToDurationOption(meta.duration)
            setDuration(snappedDuration)
            const snappedEnd = moment(range.start).add(snappedDuration, 'minutes').toDate()
            applyRangeToForm({ start: range.start, end: snappedEnd })
        } else {
            applyRangeToForm(range)
        }
        setConflictInfo(conflict || { hasConflicts: false, message: '' })
    }

    const syncRangeFromForm = (nextStartDate: string, nextStartTime: string, nextEndTime: string) => {
        if (!nextStartDate || !nextStartTime || !nextEndTime) return
        const startM = moment.tz(`${nextStartDate} ${nextStartTime}`, 'YYYY-MM-DD HH:mm', timezone)
        const endM = moment.tz(`${nextStartDate} ${nextEndTime}`, 'YYYY-MM-DD HH:mm', timezone)
        if (!endM.isAfter(startM)) return
        setSelectedRange({ start: startM.toDate(), end: endM.toDate() })
    }

    const handleDurationChange = (value: number) => {
        setDuration(value)
        if (!startTime) return
        const date = startDate || moment(currentDate).format('YYYY-MM-DD')
        const nextEnd = moment(startTime, 'HH:mm').add(value, 'minutes').format('HH:mm')
        setEndTime(nextEnd)
        if (!startDate) setStartDate(date)
        syncRangeFromForm(date, startTime, nextEnd)
    }

    const handleStartTimeChange = (value: string) => {
        setStartTime(value)
        if (!startDate) {
            setStartDate(moment(currentDate).format('YYYY-MM-DD'))
        }
        const date = startDate || moment(currentDate).format('YYYY-MM-DD')
        let nextEnd = endTime
        if (value && (!endTime || !moment(endTime, 'HH:mm').isAfter(moment(value, 'HH:mm')))) {
            nextEnd = moment(value, 'HH:mm').add(duration, 'minutes').format('HH:mm')
            setEndTime(nextEnd)
        }
        syncRangeFromForm(date, value, nextEnd || endTime)
    }

    const handleEndTimeChange = (value: string) => {
        setEndTime(value)
        const date = startDate || moment(currentDate).format('YYYY-MM-DD')
        if (!startDate) setStartDate(date)
        syncRangeFromForm(date, startTime, value)
    }

    const calculatedDuration = useMemo(() => {
        if (!startDate || !startTime || !endTime) return duration
        const startM = moment.tz(`${startDate} ${startTime}`, 'YYYY-MM-DD HH:mm', timezone)
        const endM = moment.tz(`${startDate} ${endTime}`, 'YYYY-MM-DD HH:mm', timezone)
        if (!endM.isAfter(startM)) return duration
        return endM.diff(startM, 'minutes')
    }, [startDate, startTime, endTime, timezone, duration])

    const isValidSelection = useMemo(() => {
        if (!startDate || !startTime || !endTime) return false
        const startM = moment.tz(`${startDate} ${startTime}`, 'YYYY-MM-DD HH:mm', timezone)
        const endM = moment.tz(`${startDate} ${endTime}`, 'YYYY-MM-DD HH:mm', timezone)
        return endM.isAfter(startM) && !startM.isBefore(moment())
    }, [startDate, startTime, endTime, timezone])

    const handleSchedule = () => {
        if (!isValidSelection) return
        const startDateTime = moment.tz(`${startDate} ${startTime}`, 'YYYY-MM-DD HH:mm', timezone).format('YYYY-MM-DDTHH:mm:ss')
        const endDateTime = moment.tz(`${startDate} ${endTime}`, 'YYYY-MM-DD HH:mm', timezone).format('YYYY-MM-DDTHH:mm:ss')

        setDateTimeSelected((prev: DateTimeObj) => ({
            ...prev,
            date: startDateTime,
            time: startDateTime,
            endTime: endDateTime,
            duration: calculatedDuration,
            timezone,
        }))
        setConfirmationModalOpen(true)
        onClose()
    }

    const handleNavigate = (date: Date) => {
        setCurrentDate(date)
    }

    const handleClearSelection = () => {
        setSelectedRange(null)
        setStartDate('')
        setStartTime('')
        setEndTime('')
        setConflictInfo({ hasConflicts: false, message: '' })
    }

    const hasSelection = Boolean(selectedRange || startTime || endTime)

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth={false}
            PaperProps={{
                sx: {
                    width: 'min(920px, 96vw)',
                    maxHeight: '92vh',
                    borderRadius: '16px',
                    border: '1px solid color-mix(in srgb, var(--primary) 28%, var(--border))',
                    boxShadow: '0 0 0 1px color-mix(in srgb, var(--primary) 20%, transparent), 0 20px 40px rgba(0,0,0,0.12)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'var(--page)',
                },
            }}
        >
            <div className="flex flex-col bg-page min-h-0 max-h-[92vh]">
                <div className="flex items-center justify-between px-6 pt-4 pb-3 shrink-0">
                    <h2 className="text-xl font-semibold text-foreground m-0">Select Slot</h2>
                    <IconButton onClick={onClose} size="small" aria-label="Close">
                        <LuX className="text-base!" />
                    </IconButton>
                </div>

                <div className="px-6 pt-2 pb-4 flex flex-col gap-4 min-h-0 overflow-hidden">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <FormControl fullWidth size="small">
                            <InputLabel>Duration</InputLabel>
                            <Select
                                label="Duration"
                                value={duration}
                                onChange={(e) => handleDurationChange(Number(e.target.value))}
                                MenuProps={{ PaperProps: { style: { maxHeight: 280 } } }}
                            >
                                {DURATION_OPTIONS.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option} min
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                            <InputLabel shrink>Start Time</InputLabel>
                            <Select
                                label="Start Time"
                                notched
                                displayEmpty
                                value={startTime}
                                onChange={(e) => handleStartTimeChange(e.target.value)}
                                renderValue={(selected) => (selected ? TIME_OPTIONS.find((o) => o.value === selected)?.label || selected : 'Select')}
                                MenuProps={{ PaperProps: { style: { maxHeight: 280 } } }}
                            >
                                <MenuItem value="" sx={{ display: 'none' }} />
                                {TIME_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                            <InputLabel shrink>End Time</InputLabel>
                            <Select
                                label="End Time"
                                notched
                                displayEmpty
                                value={endTime}
                                onChange={(e) => handleEndTimeChange(e.target.value)}
                                renderValue={(selected) => (selected ? TIME_OPTIONS.find((o) => o.value === selected)?.label || selected : 'Select')}
                                MenuProps={{ PaperProps: { style: { maxHeight: 280 } } }}
                            >
                                <MenuItem value="" sx={{ display: 'none' }} />
                                {TIME_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small">
                            <InputLabel>Time Zone</InputLabel>
                            <Select
                                label="Time Zone"
                                value={timezone}
                                onChange={(e) => setTimezone(e.target.value)}
                                renderValue={(selected) => getTimezoneLabel(selected, { jobCountry: jobPostingCountry })}
                                MenuProps={{ PaperProps: { style: { maxHeight: 280 } } }}
                            >
                                <TimezoneSelectMenuItems jobCountry={jobPostingCountry} />
                            </Select>
                        </FormControl>
                    </div>

                    <BookWeeklyCalendar
                        availableSlots={availableSlots}
                        busyEvents={busyEvents}
                        startDate={currentDate}
                        timezone={timezone}
                        onNavigate={handleNavigate}
                        duration={duration}
                        selectedRange={selectedRange}
                        onSelectRange={handleSelectRange}
                        onClearSelection={handleClearSelection}
                    />

                    <div
                        className={`flex items-center justify-between gap-2 rounded-md border px-1.5 min-h-8 py-1.5 ${
                            hasSelection
                                ? conflictInfo.hasConflicts
                                    ? 'border-amber-300 bg-amber-50'
                                    : 'border-emerald-300 bg-emerald-100'
                                : 'border-border bg-muted'
                        }`}
                    >
                        {hasSelection ? (
                            <>
                                <div className="flex items-center gap-1.5 min-w-0">
                                    <LuCircleCheck className={`shrink-0 text-base! ${conflictInfo.hasConflicts ? 'text-amber-700' : 'text-emerald-700'}`} />
                                    <p className={`m-0 text-xs truncate ${conflictInfo.hasConflicts ? 'text-amber-950' : 'text-emerald-950'}`}>
                                        <span className="font-medium">Time selected</span>
                                        <span className={conflictInfo.hasConflicts ? 'text-amber-800' : 'text-emerald-800'}>
                                            {' · '}
                                            {selectedRange
                                                ? `${moment(selectedRange.start).format('ddd, MMM D')} · ${moment(selectedRange.start).format(TIME_FORMAT.TIME)} – ${moment(selectedRange.end).format(TIME_FORMAT.TIME)} (${formatTimezoneShort(timezone, selectedRange.start)})`
                                                : 'Ready to schedule'}
                                            {conflictInfo.hasConflicts && conflictInfo.message ? ` · ${conflictInfo.message}` : ''}
                                        </span>
                                    </p>
                                </div>
                                <Button
                                    size="small"
                                    onClick={handleClearSelection}
                                    sx={{
                                        textTransform: 'none',
                                        color: conflictInfo.hasConflicts ? 'var(--status-warning)' : 'var(--status-success)',
                                        minWidth: 0,
                                        px: 0.75,
                                        py: 0,
                                        fontSize: 12,
                                        lineHeight: 1.2,
                                        flexShrink: 0,
                                        '&:hover': {
                                            backgroundColor: conflictInfo.hasConflicts
                                                ? 'color-mix(in srgb, var(--status-warning) 18%, var(--page))'
                                                : 'color-mix(in srgb, var(--status-success) 18%, var(--page))',
                                        },
                                    }}
                                >
                                    Clear
                                </Button>
                            </>
                        ) : (
                            <div className="flex items-center gap-1.5 min-w-0">
                                <LuCalendar className="text-foreground-subtle shrink-0 text-base!" />
                                <p className="m-0 text-xs text-foreground-muted truncate">
                                    <span className="font-medium text-foreground-muted">No time selected</span>
                                    <span>{' · Click or drag on the calendar to pick a slot'}</span>
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex flex-col gap-3 px-6 py-4 border-t border-border shrink-0">
                    {!hideFaceToFace ? (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 min-w-0 min-h-10!">
                            <FormControlLabel
                                className="m-0 shrink-0"
                                control={
                                    <Switch
                                        checked={isFaceToFaceInterview}
                                        onChange={(_e, checked) => {
                                            setIsFaceToFaceInterview(checked)
                                            if (!checked) setInterviewLocation('')
                                        }}
                                        color="sanya-primary"
                                    />
                                }
                                label={<span className="text-sm text-foreground!">Is this face to face interview?</span>}
                            />
                            <AnimatePresence initial={false}>
                                {isFaceToFaceInterview && showInterviewLocation ? (
                                    <motion.div
                                        key="interview-location"
                                        className="min-w-0 w-full sm:flex-1 overflow-hidden"
                                        initial={{ opacity: 0, x: -8 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -8 }}
                                        transition={{ duration: 0.2, ease: 'easeOut' }}
                                    >
                                        <LocationPicker
                                            placeholder="e.g. Board Room, 4th Floor, Tower B, WeWork Galaxy"
                                            value={interviewLocation}
                                            onChange={setInterviewLocation}
                                            optionalHint
                                        />
                                    </motion.div>
                                ) : null}
                            </AnimatePresence>
                        </div>
                    ) : null}
                    <div className="flex items-center justify-end gap-2 shrink-0">
                        <Button variant="contained" color="neutral" disableElevation onClick={onClose} sx={{ textTransform: 'none' }}>
                            Cancel
                        </Button>
                        <Button color="sanya-primary" variant="contained" disabled={!isValidSelection} onClick={handleSchedule} className="font-semibold!">
                            Schedule
                        </Button>
                    </div>
                </div>
            </div>

            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }} open={loading}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Dialog>
    )
}

export default BookInterviewModal
